import type { AccountSummary, PasskeyItem } from '@personal-os/shared';
import { useState } from 'react';
import { passkeyAdd, passkeyOptions, passkeyRemove, passkeyRename } from './api';
import { formatWhen } from './format';
import { createPasskey, describeAuthError } from './passkeys';

export type RunSensitive = (action: () => Promise<unknown>) => Promise<void>;

export function PasskeysCard({
  account,
  run,
  refresh,
}: {
  account: AccountSummary;
  run: RunSensitive;
  refresh: () => Promise<unknown>;
}) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [label, setLabel] = useState('');
  const only = account.passkeys.length <= 1;

  async function guarded(action: () => Promise<unknown>) {
    setBusy(true);
    setMessage(null);
    try {
      await run(async () => {
        await action();
        await refresh();
      });
    } catch (err) {
      setMessage(describeAuthError(err));
    } finally {
      setBusy(false);
    }
  }

  const add = () =>
    guarded(async () => {
      const { challengeId, options } = await passkeyOptions();
      await passkeyAdd(challengeId, await createPasskey(options));
    });

  const remove = (p: PasskeyItem) =>
    guarded(async () => {
      await passkeyRemove(p.id);
    });

  async function saveName(p: PasskeyItem) {
    const next = label.trim();
    if (!next) return;
    setBusy(true);
    try {
      await passkeyRename(p.id, next);
      await refresh();
      setRenaming(null);
    } catch (err) {
      setMessage(describeAuthError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card" aria-labelledby="passkeys-title" data-testid="passkeys">
      <h2 id="passkeys-title" className="card-heading">
        Passkeys
      </h2>
      <p className="hint">
        How you sign in with Face ID or Windows Hello. Keep at least one; add one for each device
        you use.
      </p>
      <ul className="rows">
        {account.passkeys.map((p) => (
          <li key={p.id}>
            {renaming === p.id ? (
              <form
                className="inline-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  void saveName(p);
                }}
              >
                <label className="sr-only" htmlFor={`name-${p.id}`}>
                  Passkey name
                </label>
                <input
                  id={`name-${p.id}`}
                  type="text"
                  maxLength={40}
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                />
                <button type="submit" disabled={busy}>
                  Save
                </button>
                <button type="button" onClick={() => setRenaming(null)}>
                  Cancel
                </button>
              </form>
            ) : (
              <>
                <div>
                  <strong>{p.label}</strong>
                  <p className="hint">
                    {p.backedUp ? 'Synced passkey' : 'This device only'} · added{' '}
                    {formatWhen(p.createdAt)} · last used {formatWhen(p.lastUsedAt)}
                  </p>
                </div>
                <div className="button-row">
                  <button
                    type="button"
                    onClick={() => {
                      setRenaming(p.id);
                      setLabel(p.label);
                    }}
                  >
                    Rename
                  </button>
                  <button type="button" disabled={busy || only} onClick={() => void remove(p)}>
                    Remove
                  </button>
                </div>
              </>
            )}
          </li>
        ))}
      </ul>
      <button type="button" className="primary" disabled={busy} onClick={() => void add()}>
        Add a passkey
      </button>
      {message && (
        <p role="alert" className="error-text">
          {message}
        </p>
      )}
    </section>
  );
}
